/** "X citations" button. Reads the citation store rather than taking references as props. */

import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
  type AvatarGroupProps,
} from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import { cn, getFaviconUrl } from '@heidi/utils';
import { FormattedMessage } from 'react-intl';

import type { EnrichedReference } from '../../contracts';
import { evidenceCitationsMessages } from './citations.messages';
import {
  useAreEvidenceCitationRunsClaimed,
  useEvidenceCitations,
} from './citationsStore';

import type { JSX } from 'react';

/** Logos shown before the count. Three is the design; the rest are implied by it. */
const MAX_LOGOS = 3;

export interface EvidenceCitationsButtonProps {
  /** Evidence child runs this button opens. */
  runIds: readonly string[];
  /** Press handler. The host closes when `isOpen` is already true. */
  onOpen: (runIds: string[]) => void;
  /** True while the surface this button opened is showing. */
  isOpen?: boolean;
  /** Hide when a turn-level action row already offers these runs. */
  hideWhenClaimed?: boolean;
  /** Ring color behind overlapping logos. */
  ringBackground?: AvatarGroupProps['ringBackground'];
  className?: string;
}

/** Highest-authority sources first, one per domain. */
const pickLogoSources = (references: EnrichedReference[]): EnrichedReference[] => {
  const byAuthority = references.toSorted(
    (a, b) => (b.authoritativeness_score ?? 0) - (a.authoritativeness_score ?? 0)
  );

  const seen = new Set<string>();
  const picked: EnrichedReference[] = [];
  for (const reference of byAuthority) {
    const domain = reference.domain || reference.sourceName || reference.title;
    if (seen.has(domain)) continue;
    seen.add(domain);
    picked.push(reference);
    if (picked.length === MAX_LOGOS) break;
  }
  return picked;
};

const SourceAvatar = ({ reference }: { reference: EnrichedReference }) => {
  const label = reference.sourceName || reference.domain || reference.title;
  // `bg-surface` backs transparent favicons; the group's ring separates them.
  return (
    <Avatar size="xs" className="bg-surface">
      {reference.url ? <AvatarImage src={getFaviconUrl(reference.url)} alt="" /> : null}
      <AvatarFallback className="text-text-tertiary">
        {label.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
};

export const EvidenceCitationsButton = ({
  runIds,
  onOpen,
  isOpen = false,
  hideWhenClaimed = false,
  ringBackground = 'surface-shell',
  className,
}: EvidenceCitationsButtonProps): JSX.Element | null => {
  const references = useEvidenceCitations(runIds);
  const isClaimed = useAreEvidenceCitationRunsClaimed(runIds);

  if (references.length === 0) return null;
  if (hideWhenClaimed && isClaimed) return null;

  const logoSources = pickLogoSources(references);

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disableAutomaticSvgSizing
      onClick={() => onOpen([...runIds])}
      aria-pressed={isOpen}
      className={cn(
        'gap-2 text-text-secondary',
        isOpen && 'bg-surface-accent-hover text-text-primary',
        className
      )}
      data-testid="evidence-citations-button"
      data-state={isOpen ? 'open' : 'closed'}
    >
      <AvatarGroup ringBackground={ringBackground}>
        {logoSources.map((reference) => (
          <SourceAvatar key={reference.url || reference.title} reference={reference} />
        ))}
      </AvatarGroup>
      <FormattedMessage
        {...evidenceCitationsMessages.citationsCount}
        values={{ count: references.length }}
      />
    </Button>
  );
};
